import React, { useEffect, useState, useCallback, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation, useNavigate } from 'react-router'
import moment from 'moment'

// Components
import HeadingButton from '../../components/Atoms/KycComponent/HeadingButton'
import SearchBar from '../../components/Atoms/SearchBar/SearchBar'
import ViewHistoryModal from '../../components/Atoms/popup/ViewHistoryModal'
import ActionPopup from '../../components/Atoms/ActionPopup/ActionPopup'
import Loader from '../../components/Atoms/Loader'
import RedeemRequestMerchantCard from './components/RedeemRequestMerchantCard'
import Nodata from '../../components/Atoms/NoData/NoData'
import Pagination from '../../components/Pagination/Pagination'

// Actions
import { alertActions, coinManagementActions, kycVerificationListActions, userManagementActions } from '../../_actions'

const PAGE_SIZE = 5;

const RedeemRequestMerchant = () => {
  const dispatch = useDispatch()
  const { userManagement } = useSelector(state => state)
  const navigate = useNavigate()
  const location = useLocation()
  const queryParams = new URLSearchParams(location.search)
  const tabParam = queryParams.get('tab')

  const [bankList, setBankList] = useState([])
  const [activeContent, setActiveContent] = useState(tabParam || 'pending')
  const [activeButton, setActiveButton] = useState(tabParam || 'pending')
  const [pageNo, setPageNo] = useState(1)
  const [keyWord, setKeyWord] = useState('')
  const [error, setError] = useState({})
  const [showActionPopup, setShowActionPopup] = useState(false)
  const [selectedData, setSelectedData] = useState({
    status: "",
    id: "",
    rejectionReason: ""
  })
  const [count, setCount] = useState([])
  const [isViewModal, setIsViewModal] = useState(false)
  const [bankViewData, setBankViewData] = useState([])
  const [isLoading, setIsLoading] = useState(false)

  const getCountList = useCallback(() => {
    coinManagementActions.getAmountAndCount({ type: "BANK_APPROVAL_MERCHANT" })
      .then((res) => {
        if (res?.data) {
          setCount(res.data)
        }
      })
      .catch((err) => console.error("Error fetching count:", err))
  }, [])

  const updateUrlParams = useCallback((tab) => {
    const params = new URLSearchParams()
    params.set('tab', tab)
    navigate({
      pathname: location.pathname,
      search: params.toString()
    })
  }, [location.pathname, navigate])


  const details = useMemo(() => ({
    keyWord,
    status: activeContent,
    fromDate: "",
    toDate: "",
    sortBy: "createdAt",
    sortOrder: "desc",
    pageNo,
    size: PAGE_SIZE,
    isUserBank: false
  }), [keyWord, activeContent, pageNo])

  const getStatusDetails = useCallback((content) => {
    const statusMap = {
      'pending': { statusTitle: 'Pending' },
      'approved': { statusTitle: 'Approved' },
      'rejected': { statusTitle: 'Declined' },
      'more-info': { statusTitle: 'More Info Required' }
    }

    return statusMap[content] || statusMap.pending
  }, [])

  const handleButtonClick = useCallback((content) => {
    setActiveButton(content)
    setActiveContent(content)
    setPageNo(1) // Reset page number when changing tabs
  }, [])

  const handleStatusChange = useCallback((status, id) => {
    setSelectedData({
      status,
      id,
      rejectionReason: status !== "approved" ? "" : undefined
    })
    setShowActionPopup(true)
  }, [])


  const handleConfirm = useCallback(() => {
    const errors = {}

    if ((selectedData.status === "rejected" || selectedData.status === "more-info") &&
      !selectedData.rejectionReason?.trim()) {
      errors.inputValue = "Please provide a reason."
      setError(errors)
      return
    }

    setIsLoading(true)

    userManagementActions.updateMerchantBankDetailsStatus(selectedData)
      .then((response) => {
        if (response) {
          dispatch(userManagementActions.userBankDetailsList(details))
          dispatch(alertActions.success(response?.message || "Success."))
          getCountList()
          setSelectedData({
            status: "",
            id: "",
            rejectionReason: ""
          })
          setError({})
        }
      })
      .catch((err) => {
        dispatch(alertActions.error(err))
      })
      .finally(() => {
        setIsLoading(false)
        setShowActionPopup(false)
      })
  }, [selectedData, details, dispatch, getCountList])


  const handleViewAction = useCallback(async (id) => {
    try {
      setIsViewModal(true)
      setIsLoading(true)

      const response = await kycVerificationListActions.viewRejectionsReasonsForBank({ id })

      if (response?.data) {
        setBankViewData(response.data)
      }
    } catch (error) {
      console.error("Failed to fetch rejection reasons:", error)
    } finally {
      setIsLoading(false)
    }
  }, [])

  const filteredBankList = useMemo(() => {
    if (!bankList) return []
    return bankList.filter(ele => ele?.verifiedStatus === activeButton)
  }, [bankList, activeButton])


  useEffect(() => {
    getCountList()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    dispatch(userManagementActions.userBankDetailsList(details))
  }, [dispatch, details])

  useEffect(() => {
    setBankList(userManagement?.getUserBankDetails?.list)
  }, [userManagement?.getUserBankDetails?.list])

  useEffect(() => {
    updateUrlParams(activeButton)
  }, [activeButton, updateUrlParams])


  const { statusTitle } = getStatusDetails(activeContent)
  const updatedStatusDate = useMemo(() => moment().startOf('day').fromNow(), [])


  const renderBankCards = useMemo(() => {
    if (filteredBankList.length === 0) {
      return (
        <div className='mt-2 border border-gray-300 rounded-md'>
          <Nodata
            nodataLabel="No Data found"
            nodataDesc="There are no Data to display"
          />
        </div>
      )
    }

    return filteredBankList?.map((ele, idx) => (
      <div className='space-y-3' key={idx}>
        <RedeemRequestMerchantCard
          kycAddressDoc={ele?.passbookImage}
          accountName={ele?.accountHolderName}
          accountNumber={ele?.accountNumber}
          ifscCode={ele?.ifscCode}
          bankName={ele?.bankName}
          branch={ele?.branchName}
          swiftCode={ele?.swiftCode}
          bankAddress={ele?.branchAddress}
          statusTitle={statusTitle}
          updatedStatusDate={updatedStatusDate}
          handleReject={activeButton === 'pending' ? () => handleStatusChange("rejected", ele._id) : undefined}
          handleApprove={activeButton === 'pending' ? () => handleStatusChange("approved", ele._id) : undefined}
          handleMoreInfo={activeButton === 'pending' ? () => handleStatusChange("more-info", ele._id) : undefined}
          verifiedBy={ele?.verifiedBy}
          id={ele?._id}
          handleAction={() => handleViewAction(ele._id)}
          date={ele?.updatedAt}
          storeName={ele?.storeId?.storeName}
          storLogo={ele?.storeId?.storeLogo}
          storeURL={ele?.storeId?.storeURL}
          storeStatus={ele?.storeId?.status}
          userName={ele?.userId?.name}
          userEmail={ele?.userId?.email}
        />
      </div>
    ))
  }, [filteredBankList, activeButton, statusTitle, updatedStatusDate, handleStatusChange, handleViewAction])


  const totalPages = Math.ceil(userManagement?.getkycVerificationList?.data?.total / PAGE_SIZE) || 0

  return (
    <>
      <Loader loading={userManagement?.loading || isLoading} />

      <div className='p-6 space-y-2 overflow-hidden overflow-y-auto bg-gray-50'>
        <HeadingButton
          Heading='Redeem Bank  Request (Merchant) '
          activeContent={activeContent}
          pendingCount={count?.pendingCount}
          approvedCount={count?.approvedCount}
          declinedCount={count?.rejectedCount}
          moreInfoCount={count?.moreInfoCount}
          handleButtonClick={handleButtonClick}
          activeButton={activeButton}
        />

        <SearchBar searchTerm={keyWord} setSearchTerm={setKeyWord} />

        {renderBankCards}
      </div>

      {totalPages > 1 && (
        <Pagination
          totalPages={totalPages}
          currentPage={pageNo}
          onPageChange={setPageNo}
        />
      )}

      <ActionPopup
        inputValue={selectedData?.rejectionReason ?? ""}
        handleInputValue={(value) => setSelectedData(prev => ({ ...prev, rejectionReason: value }))}
        handleConfirm={handleConfirm}
        onClose={() => {
          setShowActionPopup(false)
          setSelectedData({ status: "", id: "", rejectionReason: "" })
          setError({})
        }}
        showActionPopup={showActionPopup}
        heading={`Are you sure you want to ${selectedData?.status}?`}
        showInput={false}
        inputPlaceholder="Enter rejection reason..."
        imageUrl="/warning.png"
        inputError={error}
        isNote={selectedData?.status === "rejected" || selectedData?.status === "more-info"}
        noteValue={selectedData?.rejectionReason}
        noteChange={(value) => setSelectedData(prev => ({ ...prev, rejectionReason: value }))}
      />

      <ViewHistoryModal
        isOpen={isViewModal}
        onClose={() => setIsViewModal(false)}
        heading="History"
        data={bankViewData}
        isNotesData={false}
        isApiResData={true}
      />
    </>
  )
}

export default RedeemRequestMerchant